import { useAlert } from "../contexts/AlertContext";
import theme, { themeMap } from "../theme";
import Resizer from "react-image-file-resizer";
import { AppName } from "./constants";
import { getTenantConfig } from "../tenantConfig";


/** Returns the current tenant app name from the environment. */
export const getAppName = (): AppName =>
  (import.meta.env.VITE_APP_NAME as AppName) ?? AppName.FaunaProtect;

/** Returns the MUI theme object for the current tenant. */
export const getAppTheme = () => themeMap[getAppName()] ?? themeMap[AppName.FaunaProtect];

/** Returns the user-facing display name for the current tenant. */
export const getAppDisplayName = (): string => getTenantConfig(getAppName()).displayName;

export function isValidHttpUrl(str: string) {
  var pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
    "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
    "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
    "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
    "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
    "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // fragment locator
  return !!pattern.test(str);
}

export function getFileExtension(filename: string) {
  return filename.split(".").pop();
}

// 24 hours time validation
export const isTimeValid = (time: string) => {
  // Regular expression for 24-hour time format (HH:mm or HH,mm) with optional leading zero for hours
  const timeRegex = /^(0?[0-9]|1\d|2[0-3])([:,])([0-5]\d)$/;
  return timeRegex.test(time);
};

// 24 hours timecreation
// if we enter 1, make it to 01:00,
// convert to 4 digit
export function isValidNumberWithDot(value: number) {
  // Regular expression to check if the number has a dot or colon after one or two digits
  let regex = /^\d{1,2}([.,])\d*$/;
  // Check if the value matches the regex
  return regex.test(value.toString());
}

// display date as dd-mm-yy format
export function formatDate(originalDateString: string) {
  const originalDate = new Date(originalDateString);
  const day = originalDate.getDate().toString().padStart(2, "0");
  const month = (originalDate.getMonth() + 1).toString().padStart(2, "0");
  const year = originalDate.getFullYear();

  return `${day}-${month}-${year}`;
}

// export function formatClientName(name: string | null) {
//   const maxLength = 25;
//   if (name?.length > maxLength) {
//     return name.substring(0, maxLength) + "...";
//   }
//   return name;
// }

export function formatClientName(name: string | null): string {
  const maxLength = 25;

  // Handle null value
  if (name === null) {
    return "";
  }

  if (name.length > maxLength) {
    return name.substring(0, maxLength) + "...";
  }

  return name;
}

// Based on the API remark_status value set the container color
// remark_status = 2 : Red
// remark_status = 1||null : Blue
export const getViewStatusColor = (status: number | null) => {
  if (status === 2 || status === 3) {
    return theme.palette.info.main;
  } else if (status === 1 || status === null) {
    return theme.palette.info.light;
  }
};

// Based on the API remark_status value set the font color
// remark_status = 2 : white
// remark_status = 1||null : Black
export const getFontStatusColor = (status: number | null) => {
  if (status === 2 || status === 3) {
    return theme.palette.secondary.main;
  } else if (status === 1 || status === null) {
    return theme.palette.secondary.dark;
  }
};

// Check whether it is Monday to Sunday
export const isWeekday = (date: Date) => {
  return true;
};

// Convert the files to base64
export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export function getLongDateWithYear(dateString: string | null): string | null {
  if (dateString) {
    const date = new Date(dateString);
    const mm = date.getMonth() + 1; // getMonth() is zero-based
    const dd = date.getDate();

    return (
      (dd > 9 ? "" : "0") +
      dd +
      "-" +
      (mm > 9 ? "" : "0") +
      mm +
      "-" +
      date.getFullYear()
    );
  } else {
    return null;
  }
}

export const handleDownload = async (
  documentUrl: string,
  documentName: string | null,
  showAlert: (
    message: string,
    type: "error" | "success" | "info" | "warning"
  ) => void,
  t: (key: string) => string,
  setIsLoading: (loading: boolean) => void
) => {
  setIsLoading(true);

  if (!documentUrl || documentUrl.trim() === "") {
    showAlert(t("common.document_is_not_available"), "info");
    setIsLoading(false);
    return;
  }

  try {
    const response = await fetch(documentUrl);
    if (!response.ok) {
      showAlert(t("common.Download_Failed"), "error");
      return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", documentName ?? "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showAlert(t("common.PDF_Success_Alert"), "success");
  } catch (error) {
    showAlert(t("common.Download_Failed"), "error");
  } finally {
    setIsLoading(false);
  }
};

export const resizeImageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    Resizer.imageFileResizer(
      file, // The file to be resized
      1000, // Max width
      1000, // Max height
      "JPEG", // Output format
      60, // Quality (percentage)
      0, // Rotation
      (base64) => {
        if (typeof base64 == "string") resolve(base64); // Resolve with Base64 string
      },
      "base64" // Output type
    );
  });
};

export const formatTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${hours.toString().padStart(2, '0')} : ${minutes.toString().padStart(2, '0')} : ${seconds.toString().padStart(2, '0')}`
}
export function delay(ms: number) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

export const checkDateisToday = (logDate: string) => {
  const today = new Date()
  const logDateObj = new Date(logDate)

  return (
    today.getFullYear() === logDateObj.getFullYear() &&
    today.getMonth() === logDateObj.getMonth() &&
    today.getDate() === logDateObj.getDate()
  )
}